import { ArrowLeft, Trophy, Heart, Sparkles, Star, Gift, PartyPopper } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function VictoryCelebrationGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-amber-700 hover:text-amber-800 hover:bg-amber-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Week
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-amber-100 rounded-full">
              <Trophy className="w-8 h-8 text-amber-600" />
            </div>
            <div>
              <h1 className="text-3xl font-serif font-bold text-gray-900">Victory Celebration</h1>
              <p className="text-amber-600">Week 12, Day 3 - Keeping Your Glow</p>
            </div>
          </div>

          <div className="prose prose-amber max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              You've made it through 12 weeks of transformation! Today, list every single win from this 
              journey - big and small. Then treat yourself to something special. You've earned it.
            </p>

            <div className="bg-amber-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-amber-800 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5" />
                Why Celebration Matters
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Releases dopamine, reinforcing positive behaviors</li>
                <li>• Fights perfectionism and "never enough" thinking</li>
                <li>• Builds self-compassion and self-worth</li>
                <li>• Acknowledges the real work you've done</li>
                <li>• Creates momentum for continued growth</li>
                <li>• Proves to yourself that change is possible</li>
              </ul>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Your Victory List</h2>
            <p className="text-gray-700 mb-4">List your wins in these categories:</p>
            
            <div className="space-y-4 mb-8">
              <div className="bg-white border-2 border-amber-200 rounded-xl p-5">
                <h3 className="font-semibold text-amber-700 mb-2 flex items-center gap-2">
                  <Star className="w-4 h-4" />
                  Habits Formed
                </h3>
                <p className="text-gray-600 text-sm mb-2">What new habits have you established?</p>
                <div className="bg-gray-50 rounded-lg p-3 text-gray-500 text-sm italic">
                  Examples: Morning meditation, daily journaling, evening skincare routine, drinking more water...
                </div>
              </div>
              
              <div className="bg-white border-2 border-amber-200 rounded-xl p-5">
                <h3 className="font-semibold text-amber-700 mb-2 flex items-center gap-2">
                  <Star className="w-4 h-4" />
                  Challenges Overcome
                </h3>
                <p className="text-gray-600 text-sm mb-2">What obstacles did you push through?</p>
                <div className="bg-gray-50 rounded-lg p-3 text-gray-500 text-sm italic">
                  Examples: Completed tasks on hard days, tried something scary, pushed past comfort zone...
                </div>
              </div>
              
              <div className="bg-white border-2 border-amber-200 rounded-xl p-5">
                <h3 className="font-semibold text-amber-700 mb-2 flex items-center gap-2">
                  <Star className="w-4 h-4" />
                  Insights Gained
                </h3>
                <p className="text-gray-600 text-sm mb-2">What have you learned about yourself?</p>
                <div className="bg-gray-50 rounded-lg p-3 text-gray-500 text-sm italic">
                  Examples: Discovered what truly relaxes you, learned your triggers, understood your needs better...
                </div>
              </div>
              
              <div className="bg-white border-2 border-amber-200 rounded-xl p-5">
                <h3 className="font-semibold text-amber-700 mb-2 flex items-center gap-2">
                  <Star className="w-4 h-4" />
                  Risks Taken
                </h3>
                <p className="text-gray-600 text-sm mb-2">Where did you step outside your comfort zone?</p>
                <div className="bg-gray-50 rounded-lg p-3 text-gray-500 text-sm italic">
                  Examples: Had a difficult conversation, tried a new activity, shared something vulnerable...
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-amber-800 mb-4 flex items-center gap-2">
                <Gift className="w-5 h-5" />
                Ways to Celebrate
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Treat yourself to something you've been wanting</li>
                <li>• Book a massage, facial, or spa treatment</li>
                <li>• Have a special meal (out or homemade)</li>
                <li>• Take yourself on a solo date</li>
                <li>• Buy yourself flowers</li>
                <li>• Share your accomplishments with someone who'll celebrate with you</li>
                <li>• Take a day off just because</li>
                <li>• Do something purely for pleasure</li>
              </ul>
            </div>

            <div className="bg-rose-50 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-rose-800 mb-4 flex items-center gap-2">
                <PartyPopper className="w-5 h-5" />
                Remember
              </h2>
              <p className="text-gray-700 mb-4">
                Every win counts, including:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• Showing up on days you didn't feel like it</li>
                <li>• Being kind to yourself when you missed something</li>
                <li>• Starting again after slipping up</li>
                <li>• Reading guides, trying new things, staying curious</li>
                <li>• Simply completing 12 weeks of intentional growth</li>
              </ul>
              <p className="text-rose-700 font-medium mt-4">
                You did something remarkable. Celebrate that.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
